import { SignUp, useUser, useAuth } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import { Leaf, Clock, CheckCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';

export default function RequestAccessPage() {
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const [isSettingRole, setIsSettingRole] = useState(false);
  const [roleSet, setRoleSet] = useState(false);

  // When user signs up, call API to set their role to 'new'
  useEffect(() => {
    async function setNewRole() {
      if (!isSignedIn || !user || isSettingRole || roleSet) return;

      const role = user?.publicMetadata?.role as string | undefined;

      // If they already have an approved role or 'pending', don't change it
      // But DO override 'owner' since they came through request-access flow
      if (role && ['admin', 'marketing', 'sales', 'pending'].includes(role)) {
        return;
      }

      // Set role to 'pending' via API
      setIsSettingRole(true);
      try {
        const token = await getToken();
        const response = await fetch(`${API_URL}/admin/request-access`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          console.log('Role set to new');
          setRoleSet(true);
          // Reload to get updated user metadata
          window.location.reload();
        } else {
          console.error('Failed to set role:', await response.text());
        }
      } catch (error) {
        console.error('Error setting role:', error);
      } finally {
        setIsSettingRole(false);
      }
    }

    setNewRole();
  }, [isSignedIn, user, getToken, isSettingRole, roleSet]);

  // If user is already signed in, show pending status
  if (isSignedIn) {
    const role = user?.publicMetadata?.role as string | undefined;

    // If they have a valid role, redirect them to main app
    if (role && ['admin', 'marketing', 'sales', 'owner'].includes(role)) {
      window.location.href = '/';
      return null;
    }

    // Show loading while setting role
    if (isSettingRole || (!role && !roleSet)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-stone-950">
          <div className="animate-spin w-8 h-8 border-4 border-sage border-t-transparent rounded-full" />
        </div>
      );
    }

    // Show pending access message
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-950 p-4">
        <div className="max-w-md w-full bg-stone-900 rounded-2xl p-8 border border-stone-800 text-center">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">Access Request Pending</h1>
          <p className="text-stone-400 mb-6">
            Your account has been created. An administrator will review your request and grant you access soon.
          </p>
          <div className="bg-stone-800/50 rounded-xl p-4 mb-6">
            <p className="text-sm text-stone-500 mb-1">Signed in as</p>
            <p className="text-white font-medium">{user?.primaryEmailAddress?.emailAddress}</p>
          </div>
          <div className="flex items-start gap-3 text-left bg-sage/10 rounded-xl p-4">
            <CheckCircle className="w-5 h-5 text-sage mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sage font-medium text-sm">What happens next?</p>
              <p className="text-stone-400 text-sm mt-1">
                Once approved, you'll be able to access the admin portal. You'll receive an email notification when your access is granted.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex w-full bg-stone-950">
      {/* Left Side - Brand */}
      <div className="hidden lg:flex w-1/2 bg-stone-900 relative flex-col justify-between p-16 text-white overflow-hidden">
        {/* Abstract Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-[20%] -left-[10%] w-[70%] h-[70%] bg-sage/30 rounded-full blur-[120px]" />
          <div className="absolute top-[40%] -right-[10%] w-[60%] h-[60%] bg-emerald-600/20 rounded-full blur-[100px]" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-sage rounded-xl flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold">SereneTouch Admin</span>
          </div>

          <h1 className="text-5xl font-bold leading-[1.1] mb-8">
            Request Access to<br/>
            <span className="text-sage">Admin Portal</span>
          </h1>
          <p className="text-lg text-stone-300 max-w-md leading-relaxed">
            Create an account to request access to the SereneTouch admin portal. An administrator will review your request.
          </p>
        </div>

        {/* Steps */}
        <div className="relative z-10">
          <div className="space-y-4">
            <div className="flex items-center gap-4 bg-white/10 rounded-lg p-4">
              <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center text-white font-bold text-sm">1</div>
              <div>
                <p className="font-medium">Create your account</p>
                <p className="text-sm text-stone-400">Sign up with Google or email</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white/10 rounded-lg p-4">
              <div className="w-8 h-8 rounded-full bg-stone-700 flex items-center justify-center text-white font-bold text-sm">2</div>
              <div>
                <p className="font-medium">Wait for approval</p>
                <p className="text-sm text-stone-400">Admin reviews your request</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-white/10 rounded-lg p-4">
              <div className="w-8 h-8 rounded-full bg-stone-700 flex items-center justify-center text-white font-bold text-sm">3</div>
              <div>
                <p className="font-medium">Get access</p>
                <p className="text-sm text-stone-400">Start using the portal</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Clerk SignUp */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-center p-4 sm:p-8 bg-stone-950 relative min-h-screen">
        {/* Mobile Header */}
        <div className="lg:hidden absolute top-8 left-8 flex items-center gap-2">
          <div className="w-8 h-8 bg-sage rounded-lg flex items-center justify-center">
            <Leaf className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">SereneTouch Admin</span>
        </div>

        <div className="w-full max-w-md bg-stone-900 rounded-2xl shadow-xl p-8 pt-12 border border-stone-800">
          <div className="mb-8 text-center">
            <div className="w-14 h-14 bg-sage/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Leaf className="w-8 h-8 text-sage" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Request Access
            </h2>
            <p className="text-stone-400">
              Create an account to get started
            </p>
          </div>

          <SignUp
            appearance={{
              baseTheme: dark,
              elements: {
                rootBox: 'w-full',
                cardBox: 'w-full',
                card: 'shadow-none bg-transparent p-0 m-0 w-full',
                main: 'w-full gap-4',
                header: 'hidden',
                headerTitle: 'hidden',
                headerSubtitle: 'hidden',
                logoBox: 'hidden',
                logoImage: 'hidden',
                socialButtons: 'w-full',
                socialButtonsBlockButton: 'border-stone-700 hover:bg-stone-800 rounded-xl h-12 font-medium overflow-visible',
                socialButtonsBlockButtonText: 'text-stone-300 font-medium',
                socialButtonsProviderIcon: 'w-5 h-5',
                alternativeMethods: 'w-full',
                alternativeMethodsBlockButton: 'w-full border-stone-700 rounded-xl',
                dividerRow: 'my-4',
                dividerLine: 'bg-stone-700',
                dividerText: 'text-stone-500 text-sm',
                form: 'w-full gap-4',
                formFieldRow: 'mb-4',
                formFieldLabel: 'text-stone-300 font-medium text-sm',
                formFieldInput: 'bg-stone-800 border-stone-700 text-white focus:ring-sage focus:border-sage rounded-xl h-12',
                formFieldInputShowPasswordButton: 'text-stone-400',
                formButtonPrimary: 'bg-sage hover:bg-sage/90 text-white shadow-lg shadow-sage/25 rounded-xl h-12 text-base font-semibold',
                footerAction: 'hidden',
                footer: 'hidden',
                identityPreview: 'bg-stone-800 border-stone-700 rounded-xl',
                identityPreviewText: 'text-white',
                identityPreviewEditButton: 'text-sage',
                footerActionLink: 'text-sage hover:text-sage/80',
              },
              layout: {
                socialButtonsPlacement: 'top',
                socialButtonsVariant: 'blockButton',
              },
            }}
            routing="hash"
            signInUrl="/sign-in"
            unsafeMetadata={{
              requestedAccess: true,
              requestedAt: new Date().toISOString(),
            }}
          />

          {/* Link back to sign in */}
          <div className="mt-6 pt-6 border-t border-stone-800 text-center">
            <p className="text-stone-500 text-sm">
              Already have access?{' '}
              <a href="/" className="text-sage hover:text-sage/80">
                Sign in
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
